// The user guide (/guide): one entry per screen, in Arabic and Hebrew.
//
// When a screen changes, update its entry here in the same change and set
// `updated` to that day (add a letter for a second change the same day:
// 2026-10-29b). The guide marks an entry «جديد» for everyone who last saw
// another `updated`. guideContent.test.js fails while a
// section in tours/sections.js has no entry and is not listed in GUIDE_PENDING.
//
// Entry: { id: section key, path, chapterId (its journey chapter, for «أرني»),
//          updated: "YYYY-MM-DD" (+ optional letter), title, summary, steps: [{ requires?, text }] }
// Texts are { ar, he }. A step's `requires` is one permission key, or an array
// of keys that are all needed; `requiresAny` is an array of which one is
// enough. Steps the user cannot do are not shown.

export const GUIDE_LANGS = ["ar", "he"];

export const GUIDE_UI = {
  ar: {
    title: "دليل المستخدم",
    intro: "شرح قصير لكل شاشة. اضغط على الشاشة للتفاصيل، و«أرني» لتجربتها على بيانات تجريبية لا تُحفظ.",
    search: "ابحث في الدليل…",
    noResults: "لا توجد نتائج.",
    showMe: "أرني",
    newBadge: "جديد",
    updated: "آخر تحديث",
    open: "فتح الشاشة",
    language: "اللغة",
    savePdf: "حفظ كملف PDF",
    making: "جارٍ التجهيز…",
    whatFor: "الشاشة",
  },
  he: {
    title: "מדריך למשתמש",
    intro: "הסבר קצר לכל מסך. לחצו על מסך לפרטים, ועל «أرني» (הראה לי) כדי לנסות אותו על נתוני הדגמה שלא נשמרים.",
    search: "חיפוש במדריך…",
    noResults: "לא נמצאו תוצאות.",
    showMe: "הראה לי",
    newBadge: "חדש",
    updated: "עודכן",
    open: "פתח את המסך",
    language: "שפה",
    savePdf: "שמירה כ-PDF",
    making: "מכין…",
    whatFor: "המסך",
  },
};

// The PDF's own fixed texts (Components/ExportPDF). Arabic uses the
// exporter's defaults.
export const GUIDE_PDF_LABELS = {
  ar: undefined,
  he: {
    issued: "תאריך הפקה",
    footerNote: "מסמך זה הופק ממערכת הניהול של העמותה, והוא מסמך פנימי.",
    preview: {
      dialog: "תצוגה מקדימה של הקובץ",
      download: "הורדת PDF",
      print: "הדפסה",
      close: "סגירה",
      trainingOnly: "תצוגה בלבד במצב תרגול",
      printFailed: "ההדפסה נכשלה. הורידו את קובץ ה-PDF והדפיסו ממנו.",
      page: (n, total) => `עמוד ${n} מתוך ${total}`,
    },
  },
};

export const GUIDE = [
  {
    id: "dashboard",
    path: "/dashboard",
    chapterId: "journey-dashboard",
    updated: "2026-10-29",
    title: { ar: "لوحة التحكم", he: "לוח הבקרה" },
    summary: {
      ar: "الشاشة الأولى بعد الدخول: أرقام سريعة، طلبات تنتظر موافقتك، ودروس اليوم.",
      he: "המסך הראשון אחרי הכניסה: מספרים מהירים, בקשות שמחכות לאישור שלך, והשיעורים של היום.",
    },
    steps: [
      {
        text: {
          ar: "أرقام سريعة عن النظام، وروابط تنقلك مباشرة إلى الأقسام.",
          he: "מספרים מהירים על המערכת, וקישורים שמובילים ישר לכל מדור.",
        },
      },
      {
        requires: ["users.view", "users.approve"],
        text: {
          ar: "حسابات جديدة: «قبول» ثم اختر دور المستخدم.",
          he: "חשבונות חדשים: «قبول» (אישור) ואז בוחרים את תפקיד המשתמש.",
        },
      },
      {
        requires: ["users.view", "users.delete"],
        text: {
          ar: "«رفض» يحذف الحساب الجديد نهائيًا.",
          he: "«رفض» (דחייה) מוחק את החשבון החדש לצמיתות.",
        },
      },
      {
        requires: ["students.view", "students.edit"],
        text: {
          ar: "طلاب سجّلهم الأهل: «قبول» ينقلهم إلى قائمة الطلاب.",
          he: "תלמידים שההורים רשמו: «قبول» מעביר אותם לרשימת התלמידים.",
        },
      },
      {
        requires: ["students.view", "students.delete"],
        text: {
          ar: "«رفض» يحذف الطالب المنتظر نهائيًا.",
          he: "«رفض» מוחק את התלמיד הממתין לצמיתות.",
        },
      },
      {
        requires: "students.parentLink",
        text: {
          ar: "«رابط تسجيل الأهل» يُرسل للأهل ليسجّلوا أبناءهم بأنفسهم.",
          he: "«رابط تسجيل الأهل» (קישור הרשמה להורים) נשלח להורים כדי שירשמו את ילדיהם בעצמם.",
        },
      },
      {
        text: {
          ar: "«دروس اليوم»: اضغط «دخول» لفتح سجل حضور الدرس.",
          he: "«دروس اليوم» (שיעורי היום): לחיצה על «دخول» פותחת את רישום הנוכחות של השיעור.",
        },
      },
    ],
  },
  {
    id: "lessons",
    path: "/lessons",
    chapterId: "journey-lessons",
    updated: "2026-10-29",
    title: { ar: "الدروس", he: "שיעורים" },
    summary: {
      ar: "برنامج الدروس الأسبوعي: عرض وفلتر، وإنشاء درس وتعديله.",
      he: "תוכנית השיעורים השבועית: צפייה וסינון, יצירת שיעור ועריכתו.",
    },
    steps: [
      {
        text: {
          ar: "اضغط «فلتر» لاختيار اليوم أو المكان أو المرشد، واضغط على درس لتفاصيله.",
          he: "מסננים לפי יום, מקום או מדריך, ולוחצים על שיעור כדי לראות את פרטיו.",
        },
      },
      {
        requires: "lessons.create",
        text: {
          ar: "«درس جديد»: الاسم، اليوم، ساعة البدء (45 دقيقة على الأقل)، المكان والمرشد.",
          he: "«درس جديد» (שיעור חדש): שם, יום, שעת התחלה (לפחות 45 דקות), מקום ומדריך.",
        },
      },
      {
        requires: "lessons.create",
        text: {
          ar: "أضف الطلاب بالاسم أو برقم الهوية، ثم «حفظ الدرس» وأكّد.",
          he: "מוסיפים תלמידים לפי שם או מספר זהות, ואז «حفظ الدرس» (שמירת השיעור) ומאשרים.",
        },
      },
      {
        requires: "lessons.edit",
        text: {
          ar: "افتح درسًا لتعديل قائمة طلابه، ولا يُحفظ شيء قبل «حفظ».",
          he: "פותחים שיעור כדי לערוך את רשימת התלמידים שלו. שום דבר לא נשמר לפני «حفظ» (שמירה).",
        },
      },
      {
        requires: ["lessons.edit", "lessons.delete"],
        text: {
          ar: "حذف الدرس نهائي ويطلب تأكيدًا.",
          he: "מחיקת שיעור היא סופית ודורשת אישור.",
        },
      },
    ],
  },
  {
    id: "calendar",
    path: "/calendar",
    chapterId: "journey-attendance",
    updated: "2026-10-29",
    title: { ar: "الحضور والغياب", he: "נוכחות" },
    summary: {
      ar: "تسجيل حضور دروس اليوم، ومراجعة الأيام السابقة.",
      he: "רישום נוכחות בשיעורי היום, וצפייה בימים קודמים.",
    },
    steps: [
      {
        text: {
          ar: "«دروس اليوم» لتسجيل الحضور، و«السجل» لمراجعته.",
          he: "«دروس اليوم» לרישום נוכחות, ו«السجل» (היסטוריה) לצפייה ברישומים.",
        },
      },
      {
        requires: "attendance.record",
        text: {
          ar: "افتح الدرس وسجّل لكل طالب: حاضر، غائب أو متأخر. الأزرار العلوية تسجّل الحالة نفسها للجميع.",
          he: "פותחים את השיעור ומסמנים לכל תלמיד: נוכח, נעדר או איחר. הכפתורים למעלה מסמנים את כולם בבת אחת.",
        },
      },
      {
        requires: "attendance.record",
        text: {
          ar: "لا يُحفظ شيء قبل «حفظ».",
          he: "שום דבר לא נשמר לפני «حفظ» (שמירה).",
        },
      },
      {
        text: {
          ar: "في «السجل» اختر الدرس ثم التاريخ لعرض حضوره.",
          he: "ב«السجل» בוחרים שיעור ואז תאריך כדי לראות את הנוכחות.",
        },
      },
    ],
  },
  {
    id: "students",
    path: "/students",
    chapterId: "journey-students",
    updated: "2026-10-29",
    title: { ar: "الطلاب", he: "תלמידים" },
    summary: {
      ar: "قائمة الطلاب وبطاقة كل طالب، وإضافة طالب جديد.",
      he: "רשימת התלמידים וכרטיס לכל תלמיד, והוספת תלמיד חדש.",
    },
    steps: [
      {
        text: {
          ar: "ابحث بالاسم أو برقم الهوية، واضغط على طالب لفتح بطاقته.",
          he: "מחפשים לפי שם או מספר זהות, ולוחצים על תלמיד כדי לפתוח את הכרטיס שלו.",
        },
      },
      {
        requires: "students.viewAll",
        text: {
          ar: "بدّل بين الطلاب الفعّالين، وبانتظار الموافقة، وغير الفعّالين.",
          he: "עוברים בין תלמידים פעילים, ממתינים לאישור ולא פעילים.",
        },
      },
      {
        requires: "students.create",
        text: {
          ar: "«إضافة طالب»: رقم الهوية أولًا، ثم الاسم وتاريخ الميلاد والجنس والسكن والمدرسة والصف، ثم احفظ وأكّد.",
          he: "«إضافة طالب» (הוספת תלמיד): קודם מספר זהות, אחר כך שם, תאריך לידה, מין, מגורים, בית ספר וכיתה, ואז שומרים ומאשרים.",
        },
      },
      {
        text: {
          ar: "في البطاقة: بيانات الطالب، وبيانات الأهل، والصحة والملاحظات. رقم الهوية لا يتغيّر.",
          he: "בכרטיס: פרטי התלמיד, פרטי ההורים, ובריאות והערות. מספר הזהות לא משתנה.",
        },
      },
      {
        requires: "students.parentLink",
        text: {
          ar: "«رابط تسجيل الأهل»: انسخه وأرسله للأهل، وطلباتهم تظهر بانتظار الموافقة. تغيير الرابط يوقف القديم.",
          he: "«رابط تسجيل الأهل» (קישור הרשמה להורים): מעתיקים ושולחים להורים, והבקשות שלהם מופיעות בהמתנה לאישור. החלפת הקישור מבטלת את הישן.",
        },
      },
      {
        requires: "students.delete",
        text: {
          ar: "حذف الطالب نهائي ويطلب تأكيدًا.",
          he: "מחיקת תלמיד היא סופית ודורשת אישור.",
        },
      },
    ],
  },
  {
    id: "reports",
    path: "/reports",
    chapterId: "journey-reports",
    updated: "2026-10-29",
    title: { ar: "التقارير", he: "דוחות" },
    summary: {
      ar: "تقارير النشاطات: كتابتها، والبحث فيها، وتصديرها كملف PDF.",
      he: "דוחות פעילות: כתיבה, חיפוש, והפקה כקובץ PDF.",
    },
    steps: [
      {
        text: {
          ar: "ابحث بالعنوان أو بالكاتب، واضغط على عنوان العمود لترتيب القائمة.",
          he: "מחפשים לפי כותרת או כותב, ולוחצים על כותרת עמודה כדי למיין.",
        },
      },
      {
        requires: "reports.create",
        text: {
          ar: "«تقرير جديد»: عنوان قصير، وعناوين ثانوية من الاقتراحات، و«صلب الموضوع» وهو مطلوب، ثم احفظ وأكّد.",
          he: "«تقرير جديد» (דוח חדש): כותרת קצרה, כותרות משנה מתוך ההצעות, ו«صلب الموضوع» (גוף הדוח) שהוא חובה, ואז שומרים ומאשרים.",
        },
      },
      {
        text: {
          ar: "«ملف التقرير» يعرض معاينة، ومنها تنزّل الملف أو تطبعه.",
          he: "«ملف التقرير» (קובץ הדוח) מציג תצוגה מקדימה, וממנה מורידים את הקובץ או מדפיסים.",
        },
      },
      {
        text: {
          ar: "في الآيفون تفتح «طباعة» عادةً نافذة المشاركة، واختر منها «طباعة». إن لم تُطبع، نزّل الملف واطبعه منه.",
          he: "באייפון, «طباعة» (הדפסה) פותח בדרך כלל את חלון השיתוף, ובוחרים בו «הדפסה». אם זה לא מדפיס, מורידים את הקובץ ומדפיסים ממנו.",
        },
      },
      {
        requires: "reports.delete",
        text: {
          ar: "حذف التقرير نهائي ويطلب تأكيدًا.",
          he: "מחיקת דוח היא סופית ודורשת אישור.",
        },
      },
    ],
  },
  {
    id: "vehicles",
    path: "/vehicles",
    chapterId: "journey-vehicles",
    updated: "2026-10-29",
    title: { ar: "المركبات", he: "רכבים" },
    summary: {
      ar: "المركبات ومواعيد الرخصة والفحص والتأمين لكل منها.",
      he: "הרכבים, ומועדי הרישיון, הטסט והביטוח של כל אחד מהם.",
    },
    steps: [
      {
        text: {
          ar: "بطاقات الأعداد في الأعلى تصفّي القائمة، ويمكن البحث برقم اللوحة أو الاسم.",
          he: "כרטיסי המספרים למעלה מסננים את הרשימה, ואפשר לחפש לפי מספר רישוי או שם.",
        },
      },
      {
        text: {
          ar: "لون كل موعد يبيّن قربه: كلما اقترب الانتهاء تغيّر اللون.",
          he: "הצבע של כל מועד מראה כמה הוא קרוב: ככל שהתפוגה מתקרבת, הצבע משתנה.",
        },
      },
      {
        text: {
          ar: "في ملف المركبة تبويب لكل من الرخصة والفحص والتأمين.",
          he: "בתיק הרכב יש לשונית לרישיון, לטסט ולביטוח.",
        },
      },
      {
        requires: "vehicles.documents",
        text: {
          ar: "«المستندات»: اعرض نسخة المستند داخل الصفحة.",
          he: "«المستندات» (מסמכים): צופים בעותק המסמך בתוך הדף.",
        },
      },
      {
        requires: "vehicles.compliance",
        text: {
          ar: "تسجيل فحص: التاريخ والنتيجة وموعد الفحص التالي، فيتحدّث التنبيه تلقائيًّا.",
          he: "רישום טסט: תאריך, תוצאה ומועד הטסט הבא, וההתראה מתעדכנת לבד.",
        },
      },
      {
        requires: "vehicles.archive",
        text: {
          ar: "الأرشفة توقف المركبة وتنبيهاتها، وتطلب تأكيدًا.",
          he: "העברה לארכיון מפסיקה את הרכב ואת ההתראות שלו, ודורשת אישור.",
        },
      },
    ],
  },
  {
    id: "files",
    path: "/files",
    chapterId: "journey-files",
    updated: "2026-10-29",
    title: { ar: "الملفات", he: "קבצים" },
    summary: {
      ar: "ملفات الجمعية ومجلداتها، ومشاركتها مع الزملاء.",
      he: "הקבצים והתיקיות של העמותה, ושיתוף שלהם עם עמיתים.",
    },
    steps: [
      {
        requires: "files.use",
        text: {
          ar: "السطر العلوي يبيّن مجلدك الحالي، وزر «معلومات التخزين» بجانبه يعرض المساحة.",
          he: "השורה העליונה מראה באיזו תיקייה אתם, והכפתור «معلومات التخزين» לידה מציג את שטח האחסון.",
        },
      },
      {
        requires: "files.use",
        text: {
          ar: "«رفع ملف» و«مجلد جديد» لإضافة الملفات وترتيبها، والبحث بالاسم أو بصاحب الملف.",
          he: "«رفع ملف» (העלאת קובץ) ו«مجلد جديد» (תיקייה חדשה) להוספה וסידור, וחיפוש לפי שם או בעלים.",
        },
      },
      {
        requires: "files.use",
        text: {
          ar: "«مشاركة»: اختر نوع الصلاحية ثم الزميل. الضغط عليه مرة أخرى يلغي المشاركة.",
          he: "«مشاركة» (שיתוף): בוחרים סוג הרשאה ואז עמית. לחיצה נוספת עליו מבטלת את השיתוף.",
        },
      },
      {
        requires: "files.use",
        text: {
          ar: "حذف مجلد يحذف كل ما فيه نهائيًا، ويطلب تأكيدًا.",
          he: "מחיקת תיקייה מוחקת לצמיתות את כל מה שבתוכה, ודורשת אישור.",
        },
      },
    ],
  },
  {
    id: "notices",
    path: "/notices",
    chapterId: "journey-notices",
    updated: "2026-10-29",
    title: { ar: "لوحة الإعلانات", he: "לוח ההודעות" },
    summary: {
      ar: "إعلانات الإدارة وتنبيهات النظام في مكان واحد.",
      he: "הודעות ההנהלה והתראות המערכת במקום אחד.",
    },
    steps: [
      {
        text: {
          ar: "الإعلانات الجديدة بخط عريض. اضغط «فلتر» لعرض غير المقروءة أو حسب الفئة أو الأهمية.",
          he: "הודעות חדשות מודגשות. מסננים לפי לא נקראו, קטגוריה או חשיבות.",
        },
      },
      {
        text: {
          ar: "«تحديد الكل كمقروء» يزيل العدد من الجرس.",
          he: "«تحديد الكل كمقروء» (סימון הכול כנקרא) מוריד את המספר מהפעמון.",
        },
      },
      {
        requires: "notices.runCheck",
        text: {
          ar: "«فحص المركبات» يبحث عن مواعيد قريبة وينشئ لها إعلانات.",
          he: "«فحص المركبات» (בדיקת רכבים) מחפש מועדים קרובים ויוצר עבורם הודעות.",
        },
      },
      {
        requires: "notices.post",
        text: {
          ar: "«إعلان جديد»: العنوان مطلوب، ثم النص والأهمية، ثم «نشر». الأرشفة تخفيه عن الجميع.",
          he: "«إعلان جديد» (הודעה חדשה): כותרת חובה, אחריה טקסט וחשיבות, ואז «نشر» (פרסום). העברה לארכיון מסתירה אותה מכולם.",
        },
      },
    ],
  },
  {
    id: "users",
    path: "/users",
    chapterId: "journey-users",
    updated: "2026-10-29",
    title: { ar: "المستخدمون", he: "משתמשים" },
    summary: {
      ar: "حسابات العاملين: الموافقة عليها، وأدوارها، وصلاحياتها.",
      he: "חשבונות הצוות: אישור, תפקידים והרשאות.",
    },
    steps: [
      {
        text: {
          ar: "ابحث بجزء من الاسم أو رقم الهوية. الحسابات: فعّال، وبانتظار الموافقة، وموقوف.",
          he: "מחפשים לפי חלק מהשם או מספר זהות. החשבונות: פעיל, ממתין לאישור, מושעה.",
        },
      },
      {
        requires: "users.approve",
        text: {
          ar: "«قبول» حساب ينتظر: اختر دورًا أدنى من دورك، ثم أكّد.",
          he: "«قبول» לחשבון ממתין: בוחרים תפקיד נמוך מהתפקיד שלכם, ומאשרים.",
        },
      },
      {
        text: {
          ar: "«تعديل» يظهر فقط للحسابات الأدنى من دورك.",
          he: "«تعديل» (עריכה) מופיע רק לחשבונות בתפקיד נמוך משלכם.",
        },
      },
      {
        requires: "users.changeRole",
        text: {
          ar: "تغيير الدور ثم «حفظ الدور». خفض الدور يطلب تأكيدًا.",
          he: "משנים תפקיד ואז «حفظ الدور» (שמירת התפקיד). הורדת תפקיד דורשת אישור.",
        },
      },
    ],
  },
  {
    id: "sports",
    path: "/sports",
    chapterId: null,
    updated: "2026-10-29a",
    title: { ar: "الرياضة", he: "ספורט" },
    summary: {
      ar: "لاعبو كرة القدم ومواعيد دروسهم وحضورهم.",
      he: "שחקני הכדורגל, מועדי האימונים והנוכחות שלהם.",
    },
    steps: [
      {
        text: {
          ar: "التبويبات: «اللاعبون»، و«مواعيد الدروس»، و«الحضور والغياب»، و«الإعلانات».",
          he: "הלשוניות: «اللاعبون» (שחקנים), «مواعيد الدروس» (מועדי אימונים), «الحضور والغياب» (נוכחות) ו«الإعلانات» (מודעות).",
        },
      },
      {
        requiresAny: ["sports.coach", "sports.manage"],
        text: {
          ar: "أضف لاعبًا من الطلاب وعدّل بطاقته، وحدّد لاعبي كل درس.",
          he: "מוסיפים שחקן מתוך התלמידים ועורכים את הכרטיס שלו, וקובעים אילו שחקנים בכל אימון.",
        },
      },
      {
        text: {
          ar: "لطالب له صورة تُصنع صورة لاعب تلقائيًا (من دون الخلفية) خلال دقيقة تقريبًا، وتظهر في بطاقته.",
          he: "לתלמיד שיש לו תמונה נוצרת אוטומטית תמונת שחקן (בלי הרקע) תוך כדקה, והיא מופיעה בכרטיס שלו.",
        },
      },
      {
        requiresAny: ["sports.coach", "sports.manage"],
        text: {
          ar: "في «الإعلانات» اكتب إعلانًا لكل اللاعبين أو للاعبي درس واحد، ثم «نشر». يظهر في موقع الرياضة ويصل تنبيه إلى هواتفهم.",
          he: "ב«الإعلانات» כותבים מודעה לכל השחקנים או לשחקני אימון אחד, ואז «نشر». היא מופיעה באתר הספורט ונשלחת התראה לטלפונים שלהם.",
        },
      },
      {
        requiresAny: ["sports.coach", "sports.manage"],
        text: {
          ar: "في «الحضور والغياب» اختر الدرس والتاريخ وسجّل، ثم «حفظ».",
          he: "ב«الحضور والغياب» בוחרים אימון ותאריך, מסמנים, ואז «حفظ».",
        },
      },
      {
        requires: "sports.manage",
        text: {
          ar: "إضافة مواعيد الدروس وتعديلها، وتبويب «إعدادات الإحصائيات».",
          he: "הוספה ועריכה של מועדי אימונים, ולשונית «إعدادات الإحصائيات» (הגדרות סטטיסטיקה).",
        },
      },
    ],
  },
];

// Sections whose entry is not written yet. Remove a section from here when
// its entry is added above.
export const GUIDE_PENDING = [];
